import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const KIND_TEXT = {
  comment: "commented on your topic",
  reply: "replied to your comment",
  topic_like: "liked your topic",
  topic_haha: "reacted to your topic",
  comment_like: "liked your comment",
  comment_haha: "reacted to your comment",
};

const ago = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const initials = (name) => {
  const [last = "?", first = ""] = (name || "").split(", ");
  return `${(first[0] || last[0] || "?")}${first ? last[0] || "" : ""}`.toUpperCase();
};

export default function NotificationBell() {
  const [data, setData] = useState({ items: [], unread_count: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const openItem = (n) => {
    setOpen(false);
    if (!n.read) api.post(`/notifications/${n.id}/read`).catch(() => {});
    setData((d) => ({
      items: d.items.map((x) => (x.id === n.id ? { ...x, read: true } : x)),
      unread_count: Math.max(0, d.unread_count - (n.read ? 0 : 1)),
    }));
    navigate(n.kind === "app" ? n.url || "/" : `/team/discussion?topic=${n.topic_id}`);
  };

  const markAll = () => {
    api.post("/notifications/read-all").catch(() => {});
    setData((d) => ({ items: d.items.map((x) => ({ ...x, read: true })), unread_count: 0 }));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative p-2 rounded-full hover:bg-summit-glacier text-gray-700 transition-colors"
          aria-label="Notifications"
          data-testid="notif-bell"
        >
          <Bell className="w-5 h-5" />
          {data.unread_count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white font-mono text-[10px] grid place-items-center"
              data-testid="notif-badge"
            >
              {data.unread_count > 9 ? "9+" : data.unread_count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] sm:w-96 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-summit-hairline">
          <span className="font-display text-lg font-semibold">Notifications</span>
          {data.unread_count > 0 && (
            <button
              type="button"
              onClick={markAll}
              className="text-xs text-summit-primary hover:underline inline-flex items-center gap-1"
              data-testid="notif-mark-all"
            >
              <Check className="w-3.5 h-3.5" /> Mark all as read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto" data-testid="notif-list">
          {data.items.length === 0 && (
            <div className="py-10 text-center text-sm text-gray-400" data-testid="notif-empty">
              No notifications yet
            </div>
          )}
          {data.items.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => openItem(n)}
              className={`w-full text-left px-4 py-3 flex gap-3 items-start hover:bg-summit-glacier transition-colors ${n.read ? "" : "bg-sky-50/70"}`}
              data-testid={`notif-item-${n.id}`}
            >
              <div className="w-9 h-9 rounded-full bg-summit-primary text-white grid place-items-center font-mono text-[11px] shrink-0">
                {n.kind === "app" ? <Bell className="w-4 h-4" /> : initials(n.actor_name)}
              </div>
              <div className="flex-1 min-w-0">
                {n.kind === "app" ? (
                  <>
                    <p className="text-sm leading-snug font-semibold">{n.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm leading-snug">
                      <span className="font-semibold">{n.actor_name}</span>{" "}
                      <span className="text-gray-600">{KIND_TEXT[n.kind] || n.kind}</span>
                    </p>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      “{n.topic_title}”{n.preview ? ` — ${n.preview}` : ""}
                    </p>
                  </>
                )}
                <p className={`font-mono text-[10px] mt-0.5 ${n.read ? "text-gray-400" : "text-sky-600 font-semibold"}`}>
                  {ago(n.created_at)}
                </p>
              </div>
              {!n.read && <span className="w-2.5 h-2.5 rounded-full bg-sky-500 shrink-0 mt-1.5" data-testid={`notif-unread-dot-${n.id}`} />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
